import type { UiConfigBase } from "@repo/ui-config";

type AppUiConfigBase = Omit<UiConfigBase, "lenses"> & {
  lenses: UiConfigBase["lenses"] & {
    language: { locale: "en" };
    numberFormat: { mode: "auto" };
  };
};

/**
 * The shipped default UI configuration (the "base" side of the
 * base ⊕ overlay merge). Versioned with the app: adding/removing/reordering
 * a region here IS the base upgrade path — user overlays re-anchor to it
 * at merge time, so edit freely.
 */

export const DASHBOARD_MAIN_SURFACE_ID = "dashboard.main";

export const DASHBOARD_MAIN_REGION_IDS = {
  addonWidgets: "addon-widgets",
  assetClasses: "asset-classes",
  commandDeck: "command-deck",
  opsQueue: "ops-queue",
  portfolio: "portfolio",
} as const;

export const BASE_UI_CONFIG = {
  schemaVersion: 1,
  lenses: {
    privacy: { enabled: false },
    denomination: { currency: "USD" },
    density: { mode: "comfortable" },
    language: { locale: "en" },
    numberFormat: { mode: "auto" },
  },
  surfaces: {
    [DASHBOARD_MAIN_SURFACE_ID]: {
      items: [
        DASHBOARD_MAIN_REGION_IDS.commandDeck,
        DASHBOARD_MAIN_REGION_IDS.assetClasses,
        DASHBOARD_MAIN_REGION_IDS.portfolio,
        DASHBOARD_MAIN_REGION_IDS.addonWidgets,
        DASHBOARD_MAIN_REGION_IDS.opsQueue,
      ],
    },
  },
} satisfies AppUiConfigBase;

/**
 * The shared ui-config package does not yet model the app-only language and
 * number-format lenses in its strict base schema. Keep its input narrowed
 * while the app augments the resolved document below; server overlays are
 * loose and round-trip both keys unchanged.
 */
export const SHARED_BASE_UI_CONFIG: UiConfigBase = {
  ...BASE_UI_CONFIG,
  lenses: {
    density: BASE_UI_CONFIG.lenses.density,
    denomination: BASE_UI_CONFIG.lenses.denomination,
    privacy: BASE_UI_CONFIG.lenses.privacy,
  },
};
